import React, { useState, useEffect } from 'react';
import { getSettings } from '../services/api';
import { adminUpdateSettings } from '../services/adminApi';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2, Plus, Upload, Loader2 } from 'lucide-react';
import { adminUploadFile } from '../services/adminApi';

const SortableHeroItem = ({ id, value, index, onChange, onRemove, onUpload, uploading }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const fileInputRef = React.useRef(null);
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="admin-hero-row" data-testid={`hero-row-${index}`}>
      <button
        type="button"
        className="admin-hero-drag"
        {...attributes}
        {...listeners}
        data-testid={`hero-drag-${index}`}
        aria-label="Drag to reorder"
      >
        <GripVertical size={18} />
      </button>
      <input
        type="url"
        className="admin-form-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Paste a URL or upload an image/video →"
        data-testid={`hero-input-${index}`}
      />
      <input
        type="file"
        ref={fileInputRef}
        accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
        style={{ display: 'none' }}
        data-testid={`hero-file-input-${index}`}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onUpload(f);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        className="admin-button admin-button-secondary admin-button-sm"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        data-testid={`hero-upload-${index}`}
        title="Upload image or video (max 100MB)"
      >
        {uploading ? <Loader2 size={14} className="admin-spin" /> : <Upload size={14} />}
      </button>
      <button
        type="button"
        className="admin-button admin-button-danger admin-button-sm"
        onClick={onRemove}
        data-testid={`hero-remove-${index}`}
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
};

const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [heroIds, setHeroIds] = useState([]); // stable per-row ids for dnd-kit
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [uploadingIndex, setUploadingIndex] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await getSettings();
      setSettings(data);
      setHeroIds(data.hero_images.map((_, i) => `hero-${i}-${Math.random().toString(36).slice(2)}`));
      setLoading(false);
    } catch (err) {
      setError('Failed to load settings');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccess(false);
    setError('');
    try {
      await adminUpdateSettings({
        photographer_name: settings.photographer_name,
        tagline: settings.tagline,
        bio: settings.bio,
        email: settings.email,
        phone: settings.phone,
        social_instagram: settings.social_instagram,
        social_behance: settings.social_behance,
        social_pinterest: settings.social_pinterest,
        hero_images: settings.hero_images.filter((u) => u && u.trim()),
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleHeroImageChange = (index, value) => {
    const newHeroImages = [...settings.hero_images];
    newHeroImages[index] = value;
    setSettings({ ...settings, hero_images: newHeroImages });
  };

  const handleHeroUpload = async (index, file) => {
    setUploadingIndex(index);
    setError('');
    try {
      const result = await adminUploadFile(file);
      handleHeroImageChange(index, result.full_url);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || 'Upload failed. Images max 15MB, videos max 100MB.');
    } finally {
      setUploadingIndex(null);
    }
  };

  const addHeroImage = () => {
    setSettings({ ...settings, hero_images: [...settings.hero_images, ''] });
    setHeroIds([...heroIds, `hero-${Date.now()}-${Math.random().toString(36).slice(2)}`]);
  };

  const removeHeroImage = (index) => {
    const newImages = settings.hero_images.filter((_, i) => i !== index);
    const newIds = heroIds.filter((_, i) => i !== index);
    setSettings({ ...settings, hero_images: newImages });
    setHeroIds(newIds);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = heroIds.indexOf(active.id);
    const newIndex = heroIds.indexOf(over.id);
    setHeroIds(arrayMove(heroIds, oldIndex, newIndex));
    setSettings({
      ...settings,
      hero_images: arrayMove(settings.hero_images, oldIndex, newIndex),
    });
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <h2 className="admin-section-title">Photographer Settings</h2>

      {success && <div className="admin-success">Settings updated successfully!</div>}
      {error && <div className="login-error">{error}</div>}

      <div className="admin-card">
        <form className="admin-form" onSubmit={handleSubmit}>
          <div className="admin-form-group">
            <label className="admin-form-label">Photographer Name</label>
            <input type="text" className="admin-form-input"
              value={settings.photographer_name}
              onChange={(e) => setSettings({ ...settings, photographer_name: e.target.value })}
              required />
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Tagline</label>
            <input type="text" className="admin-form-input"
              value={settings.tagline}
              onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
              required />
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Bio</label>
            <textarea className="admin-form-textarea"
              value={settings.bio}
              onChange={(e) => setSettings({ ...settings, bio: e.target.value })}
              required rows="5" />
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Email</label>
            <input type="email" className="admin-form-input"
              value={settings.email}
              onChange={(e) => setSettings({ ...settings, email: e.target.value })}
              required />
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Phone</label>
            <input type="text" className="admin-form-input"
              value={settings.phone}
              onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
              required />
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Instagram URL</label>
            <input type="url" className="admin-form-input"
              value={settings.social_instagram}
              onChange={(e) => setSettings({ ...settings, social_instagram: e.target.value })} />
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Behance URL</label>
            <input type="url" className="admin-form-input"
              value={settings.social_behance}
              onChange={(e) => setSettings({ ...settings, social_behance: e.target.value })} />
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Pinterest URL</label>
            <input type="url" className="admin-form-input"
              value={settings.social_pinterest}
              onChange={(e) => setSettings({ ...settings, social_pinterest: e.target.value })} />
          </div>

          <h3 style={{ marginTop: '2rem', marginBottom: '0.5rem', fontSize: '1.3rem' }}>Hero Background Media</h3>
          <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.9rem' }}>
            Drag to reorder. The top item is shown first. Use the upload button to add images (max 15MB) or videos (max 100MB) from your device.
          </p>

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={heroIds} strategy={verticalListSortingStrategy}>
              <div className="admin-hero-list" data-testid="hero-list">
                {settings.hero_images.map((img, index) => (
                  <SortableHeroItem
                    key={heroIds[index]}
                    id={heroIds[index]}
                    index={index}
                    value={img}
                    onChange={(value) => handleHeroImageChange(index, value)}
                    onRemove={() => removeHeroImage(index)}
                    onUpload={(file) => handleHeroUpload(index, file)}
                    uploading={uploadingIndex === index}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          <button
            type="button"
            className="admin-button admin-button-secondary"
            style={{ marginTop: '0.75rem', marginBottom: '1.5rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
            onClick={addHeroImage}
            data-testid="add-hero-btn"
          >
            <Plus size={16} /> Add Hero Media
          </button>

          <button type="submit" className="admin-button" disabled={saving} data-testid="save-settings-btn">
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminSettings;
